import { containerLabel } from './part-location';

/**
 * The labels pinned over the org board's columns while scrolling (ISS-019): once a card's own
 * header has scrolled away, a label in its division's colour keeps saying which department (and
 * division) the column is — "Dept. 8 Disbursements", "Div. 3 Treasury". Read from the org-doc
 * cache (`orgParts`); a part's `colour` holds its board colours (`background` + text `color`).
 */
interface PlacePart {
    partKey?: string;
    key?: string;
    partType?: string;
    name?: string;
    subTitle?: string;
    mainTitle?: string;
    parentPartKey?: string;
    colour?: { background?: string; color?: string };
}

export interface BoardPlace {
    /** The card's own department (or division, on a division card). */
    title: string;
    /** The division above a department; '' on a division card. */
    subtitle: string;
    background?: string;
    text: string;
}

const keyOf = (p: PlacePart) => p.partKey || p.key || '';
const typeOf = (p: PlacePart) => (p.partType || '').toLowerCase();

const divisionLabel = (division: PlacePart) =>
    [containerLabel(division), division.mainTitle?.trim()].filter(Boolean).join(' ');

export function boardPlace(parts: PlacePart[] | undefined, partKey: string | undefined): BoardPlace | null {
    if (!parts?.length || !partKey) return null;
    const byKey = new Map(parts.map((p) => [keyOf(p), p]));
    const start = byKey.get(partKey);
    if (!start) return null;

    // The card itself and its ancestors, nearest first.
    const chain: PlacePart[] = [];
    const seen = new Set<string>();
    for (let p: PlacePart | undefined = start; p && !seen.has(keyOf(p)); ) {
        seen.add(keyOf(p));
        chain.push(p);
        p = p.parentPartKey ? byKey.get(p.parentPartKey) : undefined;
    }

    const division = chain.find((p) => typeOf(p) === 'division');
    const department = chain.find((p) => typeOf(p) === 'department');
    if (!division && !department) return null;

    const painted =
        (division?.colour?.background && division) ||
        chain.find((p) => p.colour?.background && typeOf(p) !== 'executive');
    const background = painted?.colour?.background;
    const text = painted?.colour?.color || readableTextColour(background);
    return {
        title: department ? containerLabel(department) : divisionLabel(division as PlacePart),
        subtitle: department && division ? divisionLabel(division) : '',
        ...(background ? { background } : {}),
        text,
    };
}

/** White on dark colours, near-black on light ones (hex or rgb() only; anything else gets white). */
export function readableTextColour(background: string | undefined): string {
    const value = (background || '').trim();
    let rgb: number[] | null = null;
    const hex = /^#?([0-9a-f]{3}|[0-9a-f]{6})$/i.exec(value);
    if (hex) {
        const h = hex[1].length === 3 ? [...hex[1]].map((c) => c + c).join('') : hex[1];
        rgb = [0, 2, 4].map((i) => parseInt(h.slice(i, i + 2), 16));
    } else {
        const fn = /^rgba?\(\s*(\d+)[\s,]+(\d+)[\s,]+(\d+)/i.exec(value);
        if (fn) rgb = [fn[1], fn[2], fn[3]].map(Number);
    }
    if (!rgb) return '#ffffff';
    const lin = (c: number) => {
        const v = c / 255;
        return v <= 0.03928 ? v / 12.92 : Math.pow((v + 0.055) / 1.055, 2.4);
    };
    const luminance = 0.2126 * lin(rgb[0]) + 0.7152 * lin(rgb[1]) + 0.0722 * lin(rgb[2]);
    return luminance > 0.35 ? 'rgba(0, 0, 0, 0.87)' : '#ffffff';
}
